import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Save, Plus, Trash2, ChevronRight, GripVertical, Pause, Clock, AlertCircle, RotateCcw, X, CheckCircle2, FileText, Search, Droplet, ChevronDown, Wand2 } from 'lucide-react';

/**
 * Procedure Templates admin — per-Quotation-Type work-procedure content.
 *
 * Sprint S8b prep (2026-02c) · updated with Quotation Type dropdown
 * powered by `/api/procedure-templates/catalog` — the operator picks
 * from the real 42-item `quote_types` catalogue rather than a free-
 * text slug, so every template links directly to the ref stored on
 * `appraisals.quote_type_ref` and roof-type / category classifications
 * are surfaced next to each option.
 *
 * The left rail groups all 42 quotation types by category (Re-Roof /
 * Re-Coat / Restoration / Repairs / Guttering / Maintenance / etc.),
 * shows authored/pending state per row, and clicking a pending row
 * kicks off create-in-place. Authored rows open the editor with the
 * existing template loaded.
 */
// Coat kind + drying-required presets (Trevor's 2026-02c coat-flow
// update). Kept in sync with `backend/routes/procedure_templates.py ·
// ProcedureStep` docstring.
const COAT_KIND_OPTIONS = [
  { value: '',            label: '— not a coat step —' },
  { value: 'rust_kill',   label: 'Rust Kill' },
  { value: 'rust_prime',  label: 'Rust Prime' },
  { value: 'prime_coat',  label: 'Prime Coat' },
  { value: 'colour_coat', label: 'Colour Coat' },
];
const DRYING_OPTIONS = [
  { value: '',              label: '— no drying required —' },
  { value: 'between_coats', label: '~2 h · between coats' },
  { value: 'end_of_day',    label: '~2 h · before end of day' },
  { value: 'overnight',     label: 'overnight cure' },
  { value: 'system_spec',   label: 'per paint-system spec' },
];
// When operator picks a drying preset, auto-fill these onto the step
// unless the operator has already typed a different duration_hint.
const DRYING_PRESETS = {
  between_coats: { duration_hint: '~2 h · before next coat', set_pause: true },
  end_of_day:    { duration_hint: '~2 h · before end of day', set_pause: true },
  overnight:     { duration_hint: 'overnight', set_pause: true },
  system_spec:   { duration_hint: 'per system', set_pause: true },
};

// ─── Auto-compose client summary from steps ─────────────────────────────
// Trevor 2026-02c · steps are the source of truth · pressing "Compose
// from steps" prewrites the client_summary so it stays in sync. Pause
// steps render as indented cure/dry notes; drying_required / coat_kind
// annotations are folded into natural language so the client-facing
// text reads without jargon.
function composeSummaryFromSteps(steps, label = '') {
  const clean = (steps || []).filter(s => s && (s.text || '').trim());
  if (clean.length === 0) return '';
  const lines = [];
  lines.push(`${label ? label + ' · procedure overview:' : 'Procedure overview:'}`);
  lines.push('');
  let n = 0;
  for (const s of clean) {
    const text = s.text.trim();
    if (s.is_pause) {
      const dur = (s.duration_hint || '').trim() ? ` · ${s.duration_hint.trim()}` : '';
      lines.push(`   — pause · ${text}${dur}`);
      continue;
    }
    n += 1;
    let line = `${n}. ${text}`;
    // Coat kind tag (Rust Kill / Rust Prime / Prime Coat / Colour Coat)
    // is already implied by the step text most of the time; only append
    // when the step text doesn't already reference the coat kind.
    const coatLabel = ({
      rust_kill:   'rust kill',
      rust_prime:  'rust prime',
      prime_coat:  'prime coat',
      colour_coat: 'colour coat',
    })[s.coat_kind || ''];
    if (coatLabel && !text.toLowerCase().includes(coatLabel)) {
      line += ` (${coatLabel})`;
    }
    // Duration hint (e.g. "2 h", "24-48h") · inline in parens
    if ((s.duration_hint || '').trim()) {
      line += ` · ${s.duration_hint.trim()}`;
    }
    // Drying-required tag · describe what happens after this step
    const dryingLabel = ({
      between_coats: 'then ~2 h drying before next coat',
      end_of_day:    'then ~2 h drying before end of day',
      overnight:     'then leave to cure overnight',
      system_spec:   'then leave to dry per paint-system spec',
    })[s.drying_required || ''];
    if (dryingLabel) {
      line += ` · ${dryingLabel}`;
    }
    lines.push(line);
  }
  const hasPauses = clean.some(s => s.is_pause);
  const hasDrying = clean.some(s => s.drying_required);
  if (hasPauses || hasDrying) {
    lines.push('');
    lines.push('Timeframe · includes the drying / cure windows above so each step has clean conditions before the next.');
  }
  return lines.join('\n');
}

export default function ProcedureTemplatesPage() {
  const { api, user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  const [catalog, setCatalog] = useState([]);
  const [totalRefs, setTotalRefs] = useState(0);
  const [authoredCount, setAuthoredCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedRef, setSelectedRef] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reseeding, setReseeding] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/procedure-templates/catalog');
      setCatalog(data.items || []);
      setTotalRefs(data.total || 0);
      setAuthoredCount(data.authored || 0);
      // Preserve selection across reload
      setSelectedRef(prev => {
        if (prev && (data.items || []).some(it => it.ref === prev)) return prev;
        // Default: first authored row
        return (data.items || []).find(it => it.has_template)?.ref || (data.items || [])[0]?.ref || null;
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load procedure templates catalog');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const selectedCatalogRow = useMemo(
    () => catalog.find(it => it.ref === selectedRef) || null,
    [catalog, selectedRef]
  );

  // Load the full template document when a ref is selected + authored.
  useEffect(() => {
    if (!selectedCatalogRow || !selectedCatalogRow.has_template) {
      setSelectedTemplate(null);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/procedure-templates/by-quotation-type/${encodeURIComponent(selectedCatalogRow.ref)}`);
        setSelectedTemplate(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load template');
      }
    })();
  }, [api, selectedCatalogRow]);

  // Rehydrate draft when the selected row changes (unless dirty).
  useEffect(() => {
    if (!selectedCatalogRow) { setDraft(null); return; }
    if (dirty) return;
    if (selectedCatalogRow.has_template && !selectedTemplate) return;   // wait for full load
    if (selectedCatalogRow.has_template && selectedTemplate) {
      setDraft({
        _id: selectedTemplate._id,
        quotation_type: selectedTemplate.quotation_type,
        label: selectedTemplate.label,
        client_summary: selectedTemplate.client_summary || '',
        steps: (selectedTemplate.steps || []).map(s => ({ ...s })),
      });
    } else {
      // Draft for a new template · seeded from the catalog row
      setDraft({
        _id: null,   // marker: create-on-save
        quotation_type: selectedCatalogRow.ref,
        label: selectedCatalogRow.ref,
        client_summary: '',
        steps: [],
      });
    }
  }, [selectedCatalogRow, selectedTemplate, dirty]);

  // Group catalog rows by first category so the left rail scans easily.
  const grouped = useMemo(() => {
    const filtered = catalog.filter(it => {
      if (!searchStr.trim()) return true;
      const q = searchStr.trim().toLowerCase();
      return (
        it.ref.toLowerCase().includes(q) ||
        (it.roof_types || []).some(rt => rt.toLowerCase().includes(q)) ||
        (it.quote_categories || []).some(c => c.toLowerCase().includes(q))
      );
    });
    const g = {};
    for (const it of filtered) {
      const cat = (it.quote_categories && it.quote_categories[0]) || '(uncategorised)';
      (g[cat] = g[cat] || []).push(it);
    }
    // Sort categories in a sensible order (roof-work heavy first).
    const CATEGORY_ORDER = ['Re-Roof', 'Restoration', 'Re-Coat', 'Repairs', 'Guttering', 'Maintenance', 'New Roof', 'Appraisal', '(uncategorised)', '(orphan)'];
    return Object.entries(g).sort(
      ([a], [b]) => CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b) || a.localeCompare(b)
    );
  }, [catalog, searchStr]);

  const patch = (patch) => {
    setDraft(d => (d ? { ...d, ...patch } : d));
    setDirty(true);
  };
  const patchStep = (idx, patch) => {
    setDraft(d => {
      if (!d) return d;
      const steps = d.steps.slice();
      const merged = { ...steps[idx], ...patch };
      // When operator picks a drying-required preset, auto-flip
      // `is_pause` and pre-fill a `duration_hint` to match the label
      // (Trevor's 2026-02c coat-flow update). Operator can still
      // manually override either after — this is a helper, not a
      // hard rule.
      if ('drying_required' in patch) {
        const preset = DRYING_PRESETS[patch.drying_required];
        if (preset) {
          if (!merged.duration_hint || merged.duration_hint === '') merged.duration_hint = preset.duration_hint;
          if (preset.set_pause !== undefined) merged.is_pause = preset.set_pause;
        }
      }
      steps[idx] = merged;
      return { ...d, steps };
    });
    setDirty(true);
  };
  const addStep = (preset = null) => {
    const blank = { text: '', duration_hint: '', is_pause: false, coat_kind: '', drying_required: '' };
    let seed = blank;
    if (preset === 'coat_2h_between') {
      seed = { text: 'Coat drying · 2 h before next coat', duration_hint: '~2 h', is_pause: true, coat_kind: '', drying_required: 'between_coats' };
    } else if (preset === 'coat_2h_eod') {
      seed = { text: 'End-of-day drying · 2 h before crew off-site', duration_hint: '~2 h', is_pause: true, coat_kind: '', drying_required: 'end_of_day' };
    } else if (preset === 'overnight') {
      seed = { text: 'Overnight cure', duration_hint: 'overnight', is_pause: true, coat_kind: '', drying_required: 'overnight' };
    }
    setDraft(d => d ? { ...d, steps: [...d.steps, seed] } : d);
    setDirty(true);
  };
  const removeStep = (idx) => {
    setDraft(d => d ? { ...d, steps: d.steps.filter((_, i) => i !== idx) } : d);
    setDirty(true);
  };
  const moveStep = (idx, dir) => {
    setDraft(d => {
      if (!d) return d;
      const steps = d.steps.slice();
      const j = idx + dir;
      if (j < 0 || j >= steps.length) return d;
      [steps[idx], steps[j]] = [steps[j], steps[idx]];
      return { ...d, steps };
    });
    setDirty(true);
  };

  const save = async () => {
    if (!draft) return;
    const payload = {
      quotation_type: draft.quotation_type,
      label: draft.label,
      client_summary: draft.client_summary,
      steps: draft.steps,
    };
    try {
      setSaving(true);
      if (draft._id) {
        await api.put(`/procedure-templates/${draft._id}`, payload);
        toast.success('Saved');
      } else {
        await api.post('/procedure-templates', payload);
        toast.success('Created');
      }
      setDirty(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const revert = () => {
    setDirty(false);
    // Trigger re-hydrate by clearing selectedTemplate then re-fetching
    if (selectedCatalogRow?.has_template) {
      setSelectedTemplate(null);
      // Force reload via ref reset — small trick: touching selectedRef triggers the effect
      const r = selectedRef; setSelectedRef(null); setTimeout(() => setSelectedRef(r), 0);
    } else {
      // Fresh draft
      if (selectedCatalogRow) {
        setDraft({
          _id: null,
          quotation_type: selectedCatalogRow.ref,
          label: selectedCatalogRow.ref,
          client_summary: '',
          steps: [],
        });
      }
    }
  };

  const removeTemplate = async () => {
    if (!draft?._id) return;
    if (!window.confirm(`Delete the procedure template for "${draft.quotation_type}"?\n\nThe Work Booked auto-fill for this quotation type will fall back to blank until you re-author it.`)) return;
    try {
      setSaving(true);
      await api.delete(`/procedure-templates/${draft._id}`);
      toast.success('Deleted');
      setDirty(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const reseed = async () => {
    if (!window.confirm('Restore the 6 seed procedure templates to their original text?\n\nThis overwrites the client summary + steps on the 6 canonical templates (Ridging Bed & Point / Ridging RePoint / Re-Roof LongRun Iron / Re-Coat Iron Standard / Restore LongRun / Remedial Repairs). Any other quotation types you\'ve authored are untouched.')) return;
    try {
      setReseeding(true);
      const { data } = await api.post('/procedure-templates/seed?force=true');
      toast.success(`Reseeded ${data.seeded} of ${data.total_seeds}`);
      setDirty(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reseed failed');
    } finally {
      setReseeding(false);
    }
  };

  const trySelect = (ref) => {
    if (dirty && !window.confirm('Discard unsaved changes and switch template?')) return;
    setDirty(false);
    setSelectedTemplate(null);
    setSelectedRef(ref);
  };

  return (
    <div data-testid="procedure-templates-page" className="p-6 max-w-[1600px] mx-auto">
      <header className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Procedure Templates</h1>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Per-Quotation-Type work-procedure content · one template per <span className="font-mono px-1 py-0.5 bg-slate-100 rounded text-[10px]">quote_types.ref</span> so the S8c auto-fill on Work Booked can look this up by the exact ref stored on the appraisal.
          </p>
          <p className="text-[11px] text-slate-500 mt-1">
            <span className="font-semibold text-emerald-700">{authoredCount}</span> of <span className="font-semibold">{totalRefs}</span> authored
          </p>
        </div>
        {isAdmin && (
          <button
            data-testid="proc-reseed-btn"
            onClick={reseed}
            disabled={reseeding}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50 disabled:opacity-50"
            title="Restore the 6 seed templates to their original text"
          >
            <RotateCcw size={12} /> {reseeding ? 'Restoring…' : 'Restore seed text'}
          </button>
        )}
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[340px,1fr] gap-6">
        {/* Left rail — category-grouped catalogue */}
        <aside className="border border-slate-200 rounded-lg bg-white overflow-hidden self-start max-h-[80vh] flex flex-col">
          {/* Search box */}
          <div className="p-2 border-b border-slate-200 bg-slate-50">
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="proc-search"
                value={searchStr}
                onChange={e => setSearchStr(e.target.value)}
                placeholder="Filter by ref, roof type, category"
                className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-3 text-xs text-slate-400">Loading…</div>
            ) : grouped.length === 0 ? (
              <div className="p-3 text-xs text-slate-400 italic">No quotation types match filter</div>
            ) : grouped.map(([cat, items]) => (
              <div key={cat}>
                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-600 bg-slate-100/80 border-y border-slate-200 sticky top-0 z-10 flex items-center justify-between">
                  <span>{cat}</span>
                  <span className="text-[10px] font-mono-num text-slate-400">{items.filter(i => i.has_template).length}/{items.length}</span>
                </div>
                <ul>
                  {items.map(it => {
                    const isSel = it.ref === selectedRef;
                    return (
                      <li key={it.ref}>
                        <button
                          data-testid={`proc-row-${it.ref}`}
                          onClick={() => trySelect(it.ref)}
                          className={`w-full text-left px-3 py-2 flex items-start justify-between gap-2 border-b border-slate-100 transition-colors ${
                            isSel ? 'bg-orange-50' : 'hover:bg-slate-50'
                          }`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className={`text-xs font-semibold truncate ${isSel ? 'text-[#D35400]' : 'text-slate-800'}`}>{it.ref}</p>
                            {it.roof_types.length > 0 && (
                              <div className="flex items-center gap-1 flex-wrap mt-1">
                                {it.roof_types.slice(0, 4).map(rt => (
                                  <span key={rt} className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{rt}</span>
                                ))}
                                {it.roof_types.length > 4 && <span className="text-[9px] text-slate-400">+{it.roof_types.length - 4}</span>}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0 pt-0.5">
                            {it.has_template ? (
                              <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" title={`${it.steps_count} step(s) · ${it.summary_length} char summary`}>
                                <CheckCircle2 size={9} /> {it.steps_count}
                              </span>
                            ) : (
                              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 ring-1 ring-slate-200">
                                pending
                              </span>
                            )}
                            <ChevronRight size={12} className={isSel ? 'text-[#D35400]' : 'text-slate-300'} />
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </aside>

        {/* Right — editor */}
        <section className="border border-slate-200 rounded-lg bg-white">
          {!selectedCatalogRow || !draft ? (
            <div className="p-8 text-center text-slate-400 text-sm">
              {loading ? 'Loading…' : 'Select a quotation type from the left to edit or create'}
            </div>
          ) : (
            <div className="p-5 space-y-5">
              {/* Header row: ref pill + roof-type badges + status */}
              <div className="flex items-start gap-3 pb-3 border-b border-slate-100">
                <FileText size={18} className="text-[#D35400] shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">Quotation Type</p>
                  <p className="text-sm font-semibold text-slate-800" data-testid="proc-header-ref">{draft.quotation_type}</p>
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    {(selectedCatalogRow.roof_types || []).map(rt => (
                      <span key={rt} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 ring-1 ring-blue-200">{rt}</span>
                    ))}
                    {(selectedCatalogRow.quote_categories || []).map(c => (
                      <span key={c} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 ring-1 ring-amber-200">{c}</span>
                    ))}
                    {!draft._id && (
                      <span data-testid="proc-status-new" className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 ring-1 ring-slate-300">new · not saved</span>
                    )}
                    {selectedCatalogRow.orphan && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 ring-1 ring-rose-200" title="This template's ref no longer matches any quote_types entry. Rename or delete.">orphan</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Label */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Label · shown to operators</label>
                <input
                  data-testid="proc-edit-label"
                  disabled={!isAdmin}
                  value={draft.label}
                  onChange={e => patch({ label: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded text-sm font-semibold disabled:bg-slate-50 disabled:text-slate-500"
                />
              </div>

              {/* Client summary */}
              <div>
                <label className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                  <span>Client summary · text stamped on Work Booked</span>
                  <span className="flex items-center gap-2">
                    {isAdmin && draft.steps.length > 0 && (
                      <button
                        type="button"
                        data-testid="proc-compose-summary-btn"
                        onClick={() => {
                          const composed = composeSummaryFromSteps(draft.steps, draft.label);
                          if (!composed) {
                            toast.info('Add step text first, then compose the summary');
                            return;
                          }
                          if (draft.client_summary.trim() && !window.confirm('Overwrite the current client summary with a fresh one composed from the steps? Your existing text will be replaced.')) return;
                          patch({ client_summary: composed });
                          toast.success('Summary composed from steps · review and refine below');
                        }}
                        className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#D35400] hover:text-[#A04000] px-2 py-0.5 rounded border border-orange-200 bg-orange-50 hover:bg-orange-100 normal-case"
                        title="Auto-generate the client summary from the structured steps below"
                      >
                        <Wand2 size={10} /> Compose from steps
                      </button>
                    )}
                    <span className="text-[10px] font-mono-num text-slate-400 normal-case">{draft.client_summary.length} chars</span>
                  </span>
                </label>
                <textarea
                  data-testid="proc-edit-summary"
                  disabled={!isAdmin}
                  rows={12}
                  value={draft.client_summary}
                  onChange={e => patch({ client_summary: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded text-sm font-mono leading-relaxed disabled:bg-slate-50 disabled:text-slate-500"
                  placeholder="Concrete tile procedure for the ridges…"
                />
                <p className="text-[10px] text-slate-400 mt-1">This is the exact text that will appear in the customer-facing client-notes on the Work Booked task. Include timeframes + pauses so the client knows when to expect the crew back.</p>
              </div>

              {/* Steps */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Steps · <span className="text-slate-400 normal-case font-normal">optional structured breakdown</span>
                  </label>
                  {isAdmin && (
                    <div className="flex items-center gap-1.5">
                      <button
                        data-testid="proc-add-step"
                        type="button"
                        onClick={() => addStep()}
                        className="flex items-center gap-1 text-[11px] font-semibold text-[#D35400] hover:text-[#A04000]"
                      ><Plus size={11} /> Add step</button>
                      {/* Coat-drying quick-inserts · Trevor 2026-02c */}
                      <div className="relative group">
                        <button
                          type="button"
                          data-testid="proc-add-coat-preset-btn"
                          className="flex items-center gap-1 text-[11px] font-semibold text-cyan-700 hover:text-cyan-900 px-1.5 py-0.5 rounded border border-cyan-200 bg-cyan-50 hover:bg-cyan-100"
                          title="Insert a preset coat-drying pause step"
                        >
                          <Droplet size={10} /> Drying pause <ChevronDown size={10} />
                        </button>
                        <div className="hidden group-hover:block absolute right-0 top-full pt-1 z-30 w-52">
                          <div className="bg-white border border-slate-200 rounded shadow-lg py-1 text-[11px]">
                            <button data-testid="proc-add-coat-2h-between" type="button" onClick={() => addStep('coat_2h_between')} className="w-full text-left px-3 py-1.5 hover:bg-slate-50 flex items-center gap-1.5"><Pause size={10} className="text-amber-500" /> 2 h between coats</button>
                            <button data-testid="proc-add-coat-2h-eod" type="button" onClick={() => addStep('coat_2h_eod')} className="w-full text-left px-3 py-1.5 hover:bg-slate-50 flex items-center gap-1.5"><Pause size={10} className="text-amber-500" /> 2 h before end of day</button>
                            <button data-testid="proc-add-overnight" type="button" onClick={() => addStep('overnight')} className="w-full text-left px-3 py-1.5 hover:bg-slate-50 flex items-center gap-1.5"><Pause size={10} className="text-amber-500" /> overnight cure</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {draft.steps.length === 0 ? (
                  <p className="text-xs text-slate-400 italic px-3 py-4 border border-dashed border-slate-200 rounded">No steps yet. Add rows to build the structured breakdown.</p>
                ) : (
                  <ol className="space-y-1.5">
                    {draft.steps.map((s, idx) => (
                      <li
                        key={idx}
                        data-testid={`proc-step-row-${idx}`}
                        className={`flex items-start gap-2 px-2 py-2 rounded-md border ${s.is_pause ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}
                      >
                        <div className="flex items-center gap-0.5 pt-0.5 shrink-0">
                          <GripVertical size={12} className="text-slate-300" />
                          <span className="font-mono-num text-[10px] font-bold text-slate-500 w-4 text-right">{idx + 1}.</span>
                        </div>
                        <div className="flex-1 space-y-1.5 min-w-0">
                          <input
                            data-testid={`proc-step-text-${idx}`}
                            disabled={!isAdmin}
                            value={s.text}
                            onChange={e => patchStep(idx, { text: e.target.value })}
                            placeholder="Step description"
                            className="w-full px-2 py-1 border border-slate-200 rounded text-xs bg-white disabled:bg-slate-50 disabled:text-slate-500"
                          />
                          <div className="flex items-center gap-2 flex-wrap">
                            <label className="flex items-center gap-1 text-[11px] text-slate-600">
                              <Clock size={10} className="text-slate-400" />
                              <input
                                data-testid={`proc-step-duration-${idx}`}
                                disabled={!isAdmin}
                                value={s.duration_hint}
                                onChange={e => patchStep(idx, { duration_hint: e.target.value })}
                                placeholder="e.g. 24-48h"
                                className="w-28 px-1.5 py-0.5 border border-slate-200 rounded text-[11px] bg-white font-mono disabled:bg-slate-50 disabled:text-slate-500"
                              />
                            </label>
                            <label className="flex items-center gap-1 text-[11px] text-slate-700 font-semibold cursor-pointer select-none">
                              <input
                                data-testid={`proc-step-pause-${idx}`}
                                disabled={!isAdmin}
                                type="checkbox"
                                checked={!!s.is_pause}
                                onChange={e => patchStep(idx, { is_pause: e.target.checked })}
                                className="rounded"
                              />
                              <Pause size={10} className={s.is_pause ? 'text-amber-600' : 'text-slate-400'} />
                              <span className={s.is_pause ? 'text-amber-700' : 'text-slate-500'}>Pause · crew off-site</span>
                            </label>
                            {/* Coat-flow selects · Trevor 2026-02c */}
                            <label className="flex items-center gap-1 text-[11px] text-slate-600">
                              <Droplet size={10} className={s.coat_kind ? 'text-cyan-600' : 'text-slate-400'} />
                              <select
                                data-testid={`proc-step-coat-kind-${idx}`}
                                disabled={!isAdmin}
                                value={s.coat_kind || ''}
                                onChange={e => patchStep(idx, { coat_kind: e.target.value })}
                                className={`px-1.5 py-0.5 border rounded text-[11px] bg-white disabled:bg-slate-50 disabled:text-slate-500 ${s.coat_kind ? 'border-cyan-300 text-cyan-800 font-semibold' : 'border-slate-200'}`}
                              >
                                {COAT_KIND_OPTIONS.map(o => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                            </label>
                            <label className="flex items-center gap-1 text-[11px] text-slate-600">
                              <Clock size={10} className={s.drying_required ? 'text-amber-500' : 'text-slate-400'} />
                              <select
                                data-testid={`proc-step-drying-${idx}`}
                                disabled={!isAdmin}
                                value={s.drying_required || ''}
                                onChange={e => patchStep(idx, { drying_required: e.target.value })}
                                className={`px-1.5 py-0.5 border rounded text-[11px] bg-white disabled:bg-slate-50 disabled:text-slate-500 ${s.drying_required ? 'border-amber-300 text-amber-800 font-semibold' : 'border-slate-200'}`}
                              >
                                {DRYING_OPTIONS.map(o => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                            </label>
                          </div>
                        </div>
                        <div className="flex flex-col gap-0.5 shrink-0">
                          {isAdmin && (
                            <>
                              <button type="button" onClick={() => moveStep(idx, -1)} disabled={idx === 0} className="text-[10px] px-1 py-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move up">▲</button>
                              <button type="button" onClick={() => moveStep(idx, 1)} disabled={idx === draft.steps.length - 1} className="text-[10px] px-1 py-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move down">▼</button>
                              <button
                                data-testid={`proc-step-remove-${idx}`}
                                type="button"
                                onClick={() => removeStep(idx)}
                                className="text-red-500 hover:text-red-700 mt-0.5"
                                title="Remove step"
                              ><Trash2 size={10} /></button>
                            </>
                          )}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
                {draft.steps.some(s => s.is_pause) && (
                  <p className="text-[10px] text-amber-700 mt-2 flex items-start gap-1">
                    <AlertCircle size={10} className="mt-0.5 shrink-0" />
                    <span>Amber rows are pauses — client sees these as "crew off-site" windows. Add a duration hint so scheduling knows the gap.</span>
                  </p>
                )}
              </div>

              {/* Footer */}
              {isAdmin && (
                <div data-testid="proc-editor-footer" className="flex items-center justify-between pt-4 border-t border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="text-[11px] text-slate-500">
                      {dirty ? <span className="text-amber-600 font-semibold flex items-center gap-1"><AlertCircle size={11} /> Unsaved changes</span> : draft._id ? <span className="text-slate-400">Up to date</span> : <span className="text-slate-400">Fill in and save to author this quotation type</span>}
                    </div>
                    {draft._id && (
                      <button
                        data-testid="proc-delete-btn"
                        type="button"
                        onClick={removeTemplate}
                        disabled={saving}
                        className="flex items-center gap-1 text-[11px] px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-40"
                      ><Trash2 size={10} /> Delete</button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      data-testid="proc-revert-btn"
                      type="button"
                      disabled={!dirty || saving}
                      onClick={revert}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 border border-slate-200 text-slate-700 rounded-md hover:bg-slate-50 disabled:opacity-40"
                    ><X size={11} /> Revert</button>
                    <button
                      data-testid="proc-save-btn"
                      type="button"
                      disabled={(!dirty && !!draft._id) || saving}
                      onClick={save}
                      className="flex items-center gap-1 text-xs px-4 py-1.5 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md font-semibold disabled:opacity-40"
                    ><Save size={11} /> {saving ? 'Saving…' : draft._id ? 'Save' : 'Create'}</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
